import { dec, toFixed, type Dec } from "../domain/decimal";

/** Money is shown as "KES 1,234.50" (§2.2): the ISO code, a space, grouped digits and two
 * decimals. The currency comes from tenant settings (Kenya default), never hard-coded at call
 * sites. Intl's currency style would print "Ksh" for en-KE, which the spec does not want. */
let currency = "KES";
let locale = "en-KE";

export function configureMoney(opts: { currency?: string; locale?: string }): void {
  if (opts.currency) currency = opts.currency;
  if (opts.locale) locale = opts.locale;
}
export const currencyCode = (): string => currency;

function group(intDigits: string): string {
  // Intl handles grouping for the locale (Kenya and most markets: 1,234,567).
  try {
    return new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }).format(BigInt(intDigits));
  } catch {
    return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
}

/** "1,234.50" — grouped, exactly two decimals, no currency. */
export function formatAmount(value: number | string | Dec, places = 2): string {
  const s = toFixed(dec(value), places);
  const negative = s.startsWith("-");
  const [int = "0", frac] = (negative ? s.slice(1) : s).split(".");
  return `${negative ? "−" : ""}${group(int)}${frac !== undefined ? `.${frac}` : ""}`;
}

/** "KES 1,234.50"; negatives as "−KES 50.00". `signed` adds "+" for positives (variance). */
export function formatMoney(value: number | string | Dec | null | undefined, opts: { signed?: boolean } = {}): string {
  if (value === null || value === undefined) return "—";
  const s = toFixed(dec(value), 2);
  const negative = s.startsWith("-") && !/^-0\.00$/.test(s);
  const body = formatAmount(negative ? s.slice(1) : s.replace(/^-/, ""));
  if (negative) return `−${currency} ${body}`;
  return `${opts.signed && s !== "0.00" ? "+" : ""}${currency} ${body}`;
}

/** Whole amounts for chips and compact labels: "2,000", "KES 10". */
export function formatCompactMoney(value: number): string {
  const whole = Number.isInteger(value);
  return `${currency} ${formatAmount(value, whole ? 0 : 2)}`;
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** Litres, kg: up to 3 decimals, trailing zeros dropped. */
export function formatQty(n: number, maxPlaces = 3): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: maxPlaces }).format(n);
}

const TZ = "Africa/Nairobi";
let timeZone = TZ;
export function configureTimeZone(tz: string | undefined): void {
  timeZone = tz || TZ;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { timeZone, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
}
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { timeZone, day: "2-digit", month: "short", year: "numeric" }).format(d);
}
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit" }).format(d);
}
export function formatWeekday(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short" }).format(new Date(iso));
}

/** Never log or show a full phone number where it is not needed (§16.5): 2547•••••678. */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "—";
  const p = phone.replace(/\s+/g, "");
  if (p.length <= 6) return p;
  return `${p.slice(0, 4)}${"•".repeat(Math.max(0, p.length - 7))}${p.slice(-3)}`;
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}
